import type { ChangeEvidence, Row } from '@contract';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { CustomerChip, TransactionChip } from '@/components/money/entity-chips';
import {
  Fact,
  type Resolution,
  ResolveDialog,
} from '@/components/money/resolve-dialog';
import { Money } from '@/components/primitives/money';
import { Reference } from '@/components/primitives/reference';
import { StatusPill } from '@/components/primitives/status-pill';
import { useCanPropose } from '@/components/propose-change';
import { ReasonDialog } from '@/components/reason-dialog';
import { text } from '@/components/record-table';
import { Button } from '@/components/ui/button';
import { callAdmin } from '@/lib/api';
import { confirmSecondFactor } from '@/lib/auth';
import { formatDateTime, relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';
import { invalidate } from '@/lib/query';
import { useStaff } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

const EXPLAINED_EFFECT =
  'No money moves. It closes the break, and payouts resume if this break was holding them.';

/** What a break of this kind means, in one plain sentence. */
function breakSentence(row: Row): string {
  const detail = detailOf(row);
  const age = ageText(text(row.opened_at));
  switch (row.kind) {
    case 'payout_unconfirmed':
      return `The bank has not confirmed a payout either way. It has been waiting ${age}.`;
    case 'payout_mismatch':
      return 'The bank described a payout differently from what Sao asked for.';
    case 'payout_late_success':
      return 'A payout was paid by the bank after Sao had already given the money back to the customer.';
    case 'payout_reversed':
      return 'The bank reversed a payout after reporting it paid. The money came back to the pool.';
    case 'collection_unplaced':
      return 'A pull reached the bank, but no wallet received the money.';
    case 'collection_mismatch':
      return 'The bank described a collection differently from what Sao asked for.';
    case 'collection_reversed':
      return 'A collection was reversed by the bank after Sao had credited the wallet.';
    case 'pool_balance_mismatch':
      return 'The pool account at the bank does not match the books. Payouts are held until this is resolved.';
    case 'books_out_of_balance':
      if (detail.scope === 'wallet')
        return `One wallet's books do not match (${text(detail.issue || 'unknown').replaceAll('_', ' ')}). Only the amount in question is held on that wallet. It closes by itself after two clean hourly checks.`;
      return `Sao's own books do not balance (${text(detail.issue || 'unknown').replaceAll('_', ' ')}). Payouts are held until this is resolved.`;
    case 'core_balance_mismatch':
      return 'An account at the core does not match the books.';
    case 'outcome_conflict':
      return 'Two answers about the same payment disagree.';
    case 'duplicate_posting':
      return 'The same payment was posted twice.';
    case 'amount_mismatch':
      return 'The amount at the provider differs from the amount Sao recorded.';
    case 'account_mismatch':
      return 'The account at the provider differs from the one Sao recorded.';
    case 'unmatched_credit':
      return 'Money arrived that matches no payment.';
    case 'rail_outage':
      return `${text(detail.summary || 'A payment rail looks down')}. Payouts continue. It closes by itself at the next success on that rail.`;
    case 'pnc_mismatch':
      return 'The post-no-credit state at the core differs from Sao’s restriction.';
    default:
      return `A ${text(row.kind).replaceAll('_', ' ')} break was opened.`;
  }
}

/** The ways to close a break of this kind, and only those the API supports. */
function resolutionsFor(row: Row): Resolution[] {
  const breakId = text(row.id);
  const transactionId =
    typeof row.transaction_id === 'string' && row.transaction_id
      ? row.transaction_id
      : undefined;
  const explained: Resolution = {
    value: 'explained',
    label:
      EXPLAINED_LABEL[text(row.kind)] ?? 'It is understood: mark it explained',
    effect: EXPLAINED_EFFECT,
    kind: 'resolve_break',
    payload: { break_id: breakId },
  };
  if (row.kind === 'payout_unconfirmed' && transactionId) {
    return [
      {
        value: 'paid',
        label: 'It was paid: post it',
        effect:
          'Posts the payout as paid. The customer’s hold becomes the final debit. The break stays open until you mark it explained.',
        kind: 'resolve_payout',
        payload: { transaction_id: transactionId, outcome: 'posted' },
      },
      {
        value: 'failed',
        label: 'It failed: refund the customer',
        effect:
          'Fails the payout and gives the money back to the customer’s wallet. The break stays open until you mark it explained.',
        kind: 'resolve_payout',
        payload: { transaction_id: transactionId, outcome: 'failed' },
      },
      explained,
    ];
  }
  if (row.kind === 'payout_reversed' && transactionId) {
    return [
      {
        value: 'returned',
        label: 'The money came back from the bank: book it',
        effect:
          'Books the returned money against the payout. Needs the bank’s reference.',
        kind: 'payout_return',
        payload: { transaction_id: transactionId },
        needsReference: true,
      },
      explained,
    ];
  }
  return [explained];
}

const EXPLAINED_LABEL: Record<string, string> = {
  pool_balance_mismatch: 'The pool now matches: mark it explained',
  books_out_of_balance: 'The books are corrected: mark it explained',
  payout_late_success: 'The customer has been put right: mark it explained',
};

/** What to do first when this API has no action that fixes the cause. */
function guidance(row: Row): string | null {
  switch (row.kind) {
    case 'pool_balance_mismatch':
      return 'Book the unexplained pool statement lines first (Pool statement tab). A balance break still there at the next check opens again.';
    case 'books_out_of_balance':
      return 'Correct the books first (an adjustment from the customer’s page). A balance break still there at the next check opens again.';
    case 'payout_late_success':
    case 'payout_mismatch':
    case 'collection_unplaced':
    case 'collection_mismatch':
    case 'collection_reversed':
      return 'Resolve this at the bank, and correct the customer’s wallet with an adjustment if needed, then mark it explained. There is no action here that moves the money for this kind.';
    default:
      return null;
  }
}

function detailOf(row: Row): Record<string, unknown> {
  return typeof row.detail === 'object' && row.detail
    ? (row.detail as Record<string, unknown>)
    : {};
}

function ageText(openedAt: string): string {
  const hours = Math.max(
    0,
    Math.round((Date.now() - Date.parse(openedAt)) / 3_600_000),
  );
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} days`;
}

/** The single primary action on a break: closes it alone when it moves no money, or opens the Resolve dialog. */
export function ResolveBreak({ row }: Readonly<{ row: Row }>) {
  const [open, setOpen] = useState(false);
  const allowed = useCanPropose('resolve_break');
  const staff = useStaff();
  if (row.status !== 'open' || !allowed) return null;
  if (row.one_person_close === true) return <CloseBreakAlone row={row} />;
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Resolve…
      </Button>
      {staff.is_super_admin ? <CloseBreakAlone row={row} any /> : null}
      {open ? <BreakDialog row={row} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function BreakDialog({
  row,
  onClose,
}: Readonly<{ row: Row; onClose: () => void }>) {
  const detail = detailOf(row);
  const transactionId =
    typeof row.transaction_id === 'string' ? row.transaction_id : '';
  const customerId = typeof row.customer_id === 'string' ? row.customer_id : '';
  const evidence: ChangeEvidence[] = [{ type: 'break', ref: text(row.id) }];
  if (transactionId) evidence.push({ type: 'transaction', ref: transactionId });
  return (
    <ResolveDialog
      title="Resolve this break"
      sentence={breakSentence(row)}
      note={guidance(row)}
      hint={
        row.kind === 'payout_unconfirmed'
          ? 'Sao cannot re-check a payout from here. Check the bank first; the answer you choose is final. If it is still pending, leave this break open and come back.'
          : null
      }
      options={resolutionsFor(row)}
      evidence={evidence}
      onClose={onClose}
      facts={
        <>
          <Fact label="Problem">{text(row.kind).replaceAll('_', ' ')}</Fact>
          <Fact label="Status">
            <StatusPill status={text(row.status)} />
          </Fact>
          {typeof detail.amount_minor === 'number' ? (
            <Fact label="Amount">
              <Money
                minor={detail.amount_minor}
                currency={
                  typeof detail.currency === 'string'
                    ? detail.currency
                    : homeCurrency()
                }
              />
            </Fact>
          ) : null}
          {row.provider_ref ? (
            <Fact label="Reference">
              <Reference value={text(row.provider_ref)} />
            </Fact>
          ) : null}
          {row.provider ? (
            <Fact label="Provider">{text(row.provider)}</Fact>
          ) : null}
          {detail.bank_status || detail.provider_status ? (
            <Fact label="Last status seen">
              {text(detail.bank_status ?? detail.provider_status)}
            </Fact>
          ) : null}
          <Fact label="Opened">
            {formatDateTime(text(row.opened_at))} (
            {relativeTime(text(row.opened_at))})
          </Fact>
          <Fact label="Holds payouts">{row.holds_payouts ? 'Yes' : 'No'}</Fact>
          {transactionId ? (
            <Fact label="Transaction">
              <TransactionChip id={transactionId} />
            </Fact>
          ) : null}
          {customerId ? (
            <Fact label="Customer">
              <CustomerChip id={customerId} />
            </Fact>
          ) : null}
        </>
      }
    />
  );
}

/**
 * One person closes the break with a reason and a fresh authenticator code. For
 * a break that moves no money, any staff member with the permission; with `any`,
 * the super admin, whatever the break holds.
 */
function CloseBreakAlone({
  row,
  any = false,
}: Readonly<{ row: Row; any?: boolean }>) {
  const [open, setOpen] = useState(false);
  const close = useMutation({
    mutationFn: async ({ reason, code }: { reason: string; code: string }) => {
      await confirmSecondFactor(code);
      return callAdmin(any ? 'breaks.close_any' : 'breaks.close', {
        break_id: text(row.id),
        reason,
      });
    },
    onSuccess: async () => {
      await invalidate('money.breaks');
      setOpen(false);
      toast.success('The break is closed.');
    },
  });
  return (
    <>
      <Button
        size="sm"
        variant={any ? 'outline' : 'default'}
        onClick={() => setOpen(true)}
      >
        {any ? 'Close alone…' : 'Close break…'}
      </Button>
      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title="Close this break"
        description={
          any
            ? `${breakSentence(row)} You close the break alone as the super admin. It does not move money; payouts it was holding resume. The audit log records it and the ops alert list is told.`
            : `${breakSentence(row)} No money moves, so you close it alone. The audit log records it.`
        }
        confirmLabel="Close break"
        askCode
        pending={close.isPending}
        error={close.error?.message ?? null}
        onConfirm={(reason, code) => close.mutate({ reason, code })}
      />
    </>
  );
}
